import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { getStockTransfers, saveStockTransfer, getNextTransferNo, exportStockTransfersToExcel, exportStockTransferDetailsToExcel } from "../../components/Api/GmsStockTransfer";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getProducts } from "../../components/Api/GmsProduct";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { getTaxes } from "../../components/Api/GmsTax";
import { Eye, SquarePen, Plus, Minus, X, Download } from "lucide-react";
import StockSelectionModal from "../../components/StockSelectionModal";
import { getStockForTransfer } from "../../components/Api/GmsStockTransfer";
import StockDetailsModal from "../../components/StockDetailsModal";
import { checkStockSelectionLocks, deleteStockSelectionLock } from "../../components/Api/GmsStockSelectionLock";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components (similar to PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  StockTransfer_Id: 0,
  StockTransfer_TransferNo: "",
  StockTransfer_FromBranch_Id: null,
  StockTransfer_ToBranch_Id: null,
  StockTransfer_Date: new Date(),
  StockTransfer_TotalAmount: 0,
  StockTransfer_TaxAmount: 0,
  StockTransfer_NetAmount: 0,
  StockTransfer_TotalQty: 0,
  StockTransfer_Notes: "",
  StockTransfer_Business_Id: null,
  StockTransfer_CreatedDate: new Date().toISOString(),
  StockTransfer_CreatedBy: 1,
  Details: [],
};

const emptyFilter = {
  StockTransfer_Id: null,
  StockTransfer_TransferNo: null,
  FromBranch_Id: null,
  ToBranch_Id: null,
  Business_Id: null,
  IsReceived: null,
  FromDate: null,
  ToDate: null,
};

const defaultDetail = {
  StockTransferDet_Id: 0,
  StockTransferDet_Product_Id: null,
  StockTransferDet_Unit_Id: 1,
  StockTransferDet_Quantity: 1,
  StockTransferDet_CostPrice: 0,
  StockTransferDet_TaxAmount: 0,
  StockTransferDet_TotalAmount: 0,
  StockTransferDet_NetAmount: 0,
  Product_NameEn: "",
  Unit_NameEn: "",
  TaxDetails: [],
  StockDetails: [],
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    branchId: user.user_Branch_Id || null,
  };
};

// Generate unique form ID for stock selection lock
const generateTemporaryFormUniqueId = () => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
  const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, ''); // HHMMSS
  const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  const formId = `ST-${dateStr}-${timeStr}-${sequence}`;
  
  // Store form ID in sessionStorage so we can clean it up on refresh
  const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
  storedFormIds.push(formId);
  sessionStorage.setItem('stockLockFormIds', JSON.stringify(storedFormIds));
  
  return formId;
};

export default function StockTransferPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsStockTransfer');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsStockTransfer');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsStockTransfer');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsStockTransfer');
  const isAdmin = isUserAdmin();

  const [stockTransfers, setStockTransfers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [details, setDetails] = useState([defaultDetail]);
  
  const [isEditing, setIsEditing] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [formKey, setFormKey] = useState(0);
  // Stock selection lock - unique form ID
  const [temporaryFormUniqueId, setTemporaryFormUniqueId] = useState(null);
  
  // Modal states
  const [showStockModal, setShowStockModal] = useState(false);
  const [showStockSelectionModal, setShowStockSelectionModal] = useState(false);
  const [currentDetailIndex, setCurrentDetailIndex] = useState(0);
  const [stockDetails, setStockDetails] = useState([]);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [selectedStockItems, setSelectedStockItems] = useState({});
  
  // Stock details modal (view-only)
  const [showStockDetailsModal, setShowStockDetailsModal] = useState(false);
  const [currentStockDetails, setCurrentStockDetails] = useState([]);
  const [currentStockDetailsProduct, setCurrentStockDetailsProduct] = useState("");

  // Rounding helper
  const roundHalfUp = (value, decimals = 2) => {
    const factor = Math.pow(10, decimals);
    return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
  };

  // Format date as dd/MM/yyyy
  const formatDateDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Component render log
  console.log("🔄 StockTransferPage RENDER");
  console.log("Current form state on render:", {
    StockTransfer_Business_Id: form.StockTransfer_Business_Id,
    StockTransfer_FromBranch_Id: form.StockTransfer_FromBranch_Id,
    StockTransfer_ToBranch_Id: form.StockTransfer_ToBranch_Id,
    StockTransfer_TransferNo: form.StockTransfer_TransferNo,
    isEditing,
    isViewMode
  });

  // Define generateTransferNumber first, before it's used in useEffect and handlers
  const generateTransferNumber = useCallback(async (fromBranchId) => {
    console.log("=========================================");
    console.log("=== GENERATE TRANSFER NUMBER FUNCTION CALLED ===");
    console.log("=========================================");
    console.log("From Branch ID parameter:", fromBranchId);
    console.log("From Branch ID type:", typeof fromBranchId);
    console.log("Current Business ID (from state):", form.StockTransfer_Business_Id);
    console.log("Current form state:", {
      StockTransfer_FromBranch_Id: form.StockTransfer_FromBranch_Id,
      StockTransfer_TransferNo: form.StockTransfer_TransferNo,
      StockTransfer_Business_Id: form.StockTransfer_Business_Id
    });
    
    if (!fromBranchId) {
      console.log("❌ No From Branch ID provided, skipping transfer number generation");
      return;
    }
    
    // Note: Business ID is not required for generating transfer number (API only needs FromBranch_Id)
    // But we can log it for reference
    if (!form.StockTransfer_Business_Id) {
      console.log("⚠️ Business ID not set yet, but proceeding with transfer number generation");
      console.log("   (Transfer number only requires From Branch ID)");
    }
    
    try {
      console.log("📞 Calling API: getNextTransferNo with FromBranchId:", fromBranchId);
      console.log("   Request URL: /api/StockTransfer/get-next-transfer-no");
      console.log("   Request Payload: { FromBranch_Id:", fromBranchId, "}");
      
      const result = await getNextTransferNo(fromBranchId);
      
      console.log("📥 API Response received:", result);
      console.log("Response details:", {
        nextTransferNo: result.nextTransferNo,
        message: result.message,
        isSuccess: result.isSuccess,
        fullResponse: result
      });
      
      if (result && result.nextTransferNo && result.isSuccess) {
        console.log("✅ Transfer number generated successfully:", result.nextTransferNo);
        setForm(prev => ({
          ...prev,
          StockTransfer_TransferNo: result.nextTransferNo
        }));
      } else {
        console.log("❌ Transfer number generation failed");
        console.log("   Response:", result);
        console.log("   Message:", result?.message || "Unknown error");
        console.log("   IsSuccess:", result?.isSuccess);
        console.log("   NextTransferNo:", result?.nextTransferNo);
      }
    } catch (error) {
      console.error("❌ Error generating transfer number:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data
        }
      });
    }
  }, [form.StockTransfer_Business_Id]);

  useEffect(() => {
    if (hasPermission) {
      // Clear form and details on page load/refresh
      // IMPORTANT: This runs on every mount (including refresh)
      console.log("🔄 Clearing form on page load/refresh");
      
      // Cleanup stock locks on page load/refresh
      // Since the form is cleared, we should also clear any locks from previous session
      const cleanupLocksOnRefresh = async () => {
        try {
          const { cleanupStockSelectionLocks, deleteStockSelectionLock } = await import('../components/Api/GmsStockSelectionLock');
          
          // 1. Clean up any form IDs stored in sessionStorage (from previous session before refresh)
          const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
          console.log('🔒 Cleaning up locks for stored form IDs:', storedFormIds);
          for (const formId of storedFormIds) {
            try {
              await deleteStockSelectionLock(formId);
              console.log('✅ Cleaned up locks for form:', formId);
            } catch (err) {
              console.warn('Failed to cleanup locks for form:', formId, err);
            }
          }
          // Clear the stored form IDs
          sessionStorage.removeItem('stockLockFormIds');
          
          // 2. Also clean up locks older than 5 minutes (very short time to catch any missed ones)
          // This ensures any locks from the previous session are cleared
          await cleanupStockSelectionLocks(0.083).catch(err => { // 0.083 hours = 5 minutes
            console.warn('Failed to cleanup old locks on page load:', err);
          });
          console.log('✅ Cleaned up locks older than 5 minutes');
        } catch (error) {
          console.warn('Error during lock cleanup on page load:', error);
        }
      };
      cleanupLocksOnRefresh();
      
      const session = getUserSession();
      const initialForm = {
        ...defaultForm,
        StockTransfer_FromBranch_Id: session?.branchId || null, // Pre-fill From Branch if available
        StockTransfer_Date: new Date()
      };
      setForm(initialForm);
      setDetails([{ ...defaultDetail }]); // Create new object to ensure clean state
      setIsEditing(false);
      setTemporaryFormUniqueId(null);
      setSelectedStockItems({});
      
      loadBusinesses();
      loadBranches();
      loadProducts();
      loadUnits();
      loadTaxes();
      loadStockTransfers();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, []); // Empty dependency array - run only on mount/refresh

  // Generate transfer number when from branch is available (Business ID not required for API)
  useEffect(() => {
    console.log("=== TRANSFER NUMBER useEffect TRIGGERED ===");
    console.log("Form state:", {
      StockTransfer_Business_Id: form.StockTransfer_Business_Id,
      StockTransfer_FromBranch_Id: form.StockTransfer_FromBranch_Id,
      StockTransfer_TransferNo: form.StockTransfer_TransferNo,
      isEditing,
      isViewMode
    });
    
    const shouldGenerate = form.StockTransfer_FromBranch_Id && 
                           !form.StockTransfer_TransferNo && 
                           !isEditing && 
                           !isViewMode;
    
    console.log("Should generate transfer number?", shouldGenerate);
    console.log("Conditions:", {
      hasFromBranch: !!form.StockTransfer_FromBranch_Id,
      noTransferNo: !form.StockTransfer_TransferNo,
      notEditing: !isEditing,
      notViewMode: !isViewMode
    });
    
    if (shouldGenerate) {
      console.log("✅ CONDITIONS MET - Will generate transfer number");
      console.log("=== AUTO-GENERATE TRANSFER NUMBER (useEffect) ===");
      console.log("From Branch ID:", form.StockTransfer_FromBranch_Id);
      console.log("Current Transfer No:", form.StockTransfer_TransferNo);
      console.log("Is Editing:", isEditing);
      console.log("Is View Mode:", isViewMode);
      
      // Use a small delay to ensure state is stable
      const timer = setTimeout(() => {
        console.log("⏰ setTimeout callback executing...");
        // Use the current form state values
        const currentForm = form;
        console.log("Current form in setTimeout:", {
          StockTransfer_FromBranch_Id: currentForm.StockTransfer_FromBranch_Id,
          StockTransfer_TransferNo: currentForm.StockTransfer_TransferNo
        });
        if (currentForm.StockTransfer_FromBranch_Id) {
          console.log("📞 Calling generateTransferNumber from useEffect...");
          generateTransferNumber(currentForm.StockTransfer_FromBranch_Id);
        } else {
          console.log("❌ No From Branch ID in form state");
        }
      }, 200);
      
      return () => {
        console.log("🧹 Cleaning up timer");
        clearTimeout(timer);
      };
    } else {
      console.log("❌ CONDITIONS NOT MET - Will NOT generate transfer number");
    }
  }, [form.StockTransfer_FromBranch_Id, form.StockTransfer_TransferNo, isEditing, isViewMode, generateTransferNumber]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadStockTransfers();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({});
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load companies", "error");
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({});
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({});
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
      Swal.fire("Error", "Failed to load products", "error");
    }
  };

  const loadUnits = async () => {
    try {
      const data = await getActiveUnits();
      setUnits(data || []);
    } catch (error) {
      console.error("Error loading units:", error);
      Swal.fire("Error", "Failed to load units", "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({});
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  const loadStockTransfers = async () => {
    try {
      setLoading(true);
      const data = await getStockTransfers(filter);
      const transfersArray = Array.isArray(data) ? data : (data.data || []);
      setStockTransfers(transfersArray);
      setTotalCount(transfersArray.length);
    } catch (error) {
      console.error("Error loading stock transfers:", error);
      Swal.fire("Error", "Failed to load stock transfers", "error");
    } finally {
      setLoading(false);
    }
  };

  // Options for dropdowns
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id || b.Business_Id,
      label: b.business_NameEn || b.Business_NameEn || 'N/A'
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    return branches
      .filter(b => !form.StockTransfer_Business_Id || (b.branch_Buisness_Id || b.Branch_Buisness_Id) === form.StockTransfer_Business_Id)
      .map(b => ({
        value: b.branch_Id || b.Branch_Id,
        label: `${b.branch_Code || b.Branch_Code || ''} - ${b.branch_NameEn || b.Branch_NameEn || 'N/A'}`
      }));
  }, [branches, form.StockTransfer_Business_Id]);

  const productOptions = useMemo(() => {
    return products.map(p => ({
      value: p.product_Id || p.Product_Id,
      label: p.product_NameEn || p.Product_NameEn || 'N/A'
    }));
  }, [products]);

  const unitOptions = useMemo(() => {
    return units.map(u => ({
      value: u.unit_Id || u.Unit_Id,
      label: u.unit_NameEn || u.Unit_NameEn || 'N/A'
    }));
  }, [units]);

  const taxOptions = useMemo(() => {
    return taxes.map(t => ({
      value: t.tax_Id || t.Tax_Id,
      label: `${t.tax_NameEn || t.Tax_NameEn || 'N/A'} (${t.tax_Perc || t.Tax_Perc || 0}%)`,
      taxPercent: t.tax_Perc || t.Tax_Perc || 0
    }));
  }, [taxes]);

  const handleBusinessChange = async (businessId) => {
    console.log("=== BUSINESS CHANGED ===");
    console.log("New Business ID:", businessId);
    console.log("Current From Branch ID:", form.StockTransfer_FromBranch_Id);
    
    setForm(prev => {
      const newForm = {
        ...prev,
        StockTransfer_Business_Id: businessId,
        StockTransfer_Branch_Id: null // Reset branch when business changes
      };
      
      // Generate transfer number if From Branch is already selected
      if (prev.StockTransfer_FromBranch_Id) {
        console.log("From Branch already selected, will generate transfer number...");
        // Generate after state update
        setTimeout(() => {
          generateTransferNumber(prev.StockTransfer_FromBranch_Id);
        }, 100);
      } else {
        console.log("Waiting for From Branch to be selected...");
      }
      
      return newForm;
    });
  };

  // Generate transfer number when From Branch changes
  const handleFromBranchChange = async (branchId) => {
    console.log("=========================================");
    console.log("=== FROM BRANCH CHANGED HANDLER ===");
    console.log("=========================================");
    console.log("New From Branch ID:", branchId);
    console.log("Branch ID type:", typeof branchId);
    console.log("Current Business ID:", form.StockTransfer_Business_Id);
    console.log("Current To Branch ID:", form.StockTransfer_ToBranch_Id);
    console.log("Current form state:", {
      StockTransfer_FromBranch_Id: form.StockTransfer_FromBranch_Id,
      StockTransfer_TransferNo: form.StockTransfer_TransferNo
    });
    
    // Validate From Branch cannot be same as To Branch
    if (branchId && branchId === form.StockTransfer_ToBranch_Id) {
      Swal.fire("Error", "From Branch cannot be the same as To Branch", "error");
      return;
    }
    
    setForm(prev => {
      console.log("🔧 setForm updater function called");
      console.log("Previous form state:", {
        StockTransfer_FromBranch_Id: prev.StockTransfer_FromBranch_Id,
        StockTransfer_TransferNo: prev.StockTransfer_TransferNo
      });
      
      const newForm = {
        ...prev,
        StockTransfer_FromBranch_Id: branchId
      };
      
      console.log("New form state will be:", {
        StockTransfer_FromBranch_Id: newForm.StockTransfer_FromBranch_Id,
        StockTransfer_TransferNo: newForm.StockTransfer_TransferNo
      });
      
      if (branchId) {
        // Generate transfer number immediately (only needs branch ID)
        console.log("✅ From Branch selected, will generate transfer number...");
        console.log("Branch ID to use:", branchId);
        // Generate after state update
        setTimeout(() => {
          console.log("⏰ setTimeout in handleFromBranchChange executing...");
          console.log("Calling generateTransferNumber with:", branchId);
          generateTransferNumber(branchId);
        }, 100);
      } else {
        newForm.StockTransfer_TransferNo = "";
        console.log("❌ From Branch cleared, transfer number cleared");
      }
      
      return newForm;
    });
    
    console.log("=========================================");
  };

  const handleFormChange = (field, value) => {
    // Validate From and To Branch cannot be the same
    if (field === 'StockTransfer_ToBranch_Id' && value === form.StockTransfer_FromBranch_Id) {
      Swal.fire("Error", "To Branch cannot be the same as From Branch", "error");
      return;
    }
    if (field === 'StockTransfer_FromBranch_Id' && value === form.StockTransfer_ToBranch_Id) {
      Swal.fire("Error", "From Branch cannot be the same as To Branch", "error");
      return;
    }
    
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDetailChange = async (index, field, value) => {
    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    
    // Auto-fetch product details
    if (field === 'StockTransferDet_Product_Id' && value) {
      const product = products.find(p => (p.product_Id || p.Product_Id) === value);
      if (product) {
        const unit = units.find(u => (u.unit_Id || u.Unit_Id) === (product.unit_Id || product.Unit_Id));
        const unitName = unit ? (unit.unit_NameEn || unit.Unit_NameEn) : "Unit";
        
        // Calculate tax percentage from taxIds and populate TaxDetails
        let taxPercent = 0;
        let taxDetails = [];
        if (product.taxIds && product.taxIds.length > 0) {
          const productTaxes = taxes.filter(tax => product.taxIds.includes(tax.tax_Id || tax.Tax_Id));
          taxPercent = productTaxes.reduce((sum, tax) => sum + (tax.tax_Perc || tax.Tax_Perc || 0), 0);
          taxDetails = productTaxes.map(tax => ({
            StockTransferTaxDet_Tax_Id: tax.tax_Id || tax.Tax_Id,
            Tax_NameEn: tax.tax_NameEn || tax.Tax_NameEn,
            Tax_Perc: tax.tax_Perc || tax.Tax_Perc
          }));
        }
        
        // Get cost price from product
        const costPrice = product.product_CostPrice || product.Product_CostPrice || 0;
        
        newDetails[index] = {
          ...newDetails[index],
          StockTransferDet_CostPrice: costPrice,
          StockTransferDet_Unit_Id: product.unit_Id || product.Unit_Id || 1,
          Product_NameEn: product.product_NameEn || product.Product_NameEn,
          Unit_NameEn: unitName,
          TaxDetails: taxDetails
        };
      }
    }
    
    // Calculate amounts
    if (field === 'StockTransferDet_Quantity' || field === 'StockTransferDet_CostPrice' || field === 'StockTransferDet_TaxAmount') {
      calculateDetailAmounts(newDetails, index);
      if (isEditing) {
        recalculateTotalsFromDetails(newDetails);
      }
    }
    
    setDetails(newDetails);
  };

  const calculateDetailAmounts = (detailsArray, index) => {
    const detail = detailsArray[index];
    const quantity = parseFloat(detail.StockTransferDet_Quantity) || 0;
    const costPrice = parseFloat(detail.StockTransferDet_CostPrice) || 0;
    
    if (quantity <= 0 || costPrice <= 0) {
      detailsArray[index] = {
        ...detail,
        StockTransferDet_TotalAmount: 0,
        StockTransferDet_TaxAmount: 0,
        StockTransferDet_NetAmount: 0
      };
      return;
    }

    // Calculate total amount (quantity × cost price)
    const totalAmount = roundHalfUp(quantity * costPrice, 2);

    // Calculate tax amount
    let taxAmount = 0;
    if (Array.isArray(detail.TaxDetails) && detail.TaxDetails.length > 0) {
      taxAmount = detail.TaxDetails.reduce((sum, t) => {
        const perc = parseFloat(t.Tax_Perc || t.tax_Perc || 0) || 0;
        return sum + roundHalfUp(totalAmount * (perc / 100), 2);
      }, 0);
    }

    // Net amount = total + tax
    const netAmount = roundHalfUp(totalAmount + taxAmount, 2);

    detailsArray[index] = {
      ...detail,
      StockTransferDet_TotalAmount: totalAmount,
      StockTransferDet_TaxAmount: taxAmount,
      StockTransferDet_NetAmount: netAmount
    };
  };

  const recalculateTotalsFromDetails = (detailsArray) => {
    let totalAmount = 0;
    let totalTax = 0;
    let totalQty = 0;
    
    detailsArray.forEach((detail) => {
      if (detail && detail.StockTransferDet_Product_Id) {
        totalAmount += parseFloat(detail.StockTransferDet_TotalAmount) || 0;
        totalTax += parseFloat(detail.StockTransferDet_TaxAmount) || 0;
        totalQty += parseFloat(detail.StockTransferDet_Quantity) || 0;
      }
    });
    
    totalAmount = roundHalfUp(totalAmount, 2);
    totalTax = roundHalfUp(totalTax, 2);
    const netAmount = roundHalfUp(totalAmount + totalTax, 2);
    
    setForm(prev => ({
      ...prev,
      StockTransfer_TotalAmount: totalAmount,
      StockTransfer_TaxAmount: totalTax,
      StockTransfer_NetAmount: netAmount,
      StockTransfer_TotalQty: totalQty
    }));
  };

  const addDetail = () => {
    setDetails([...details, { ...defaultDetail }]);
  };

  const removeDetail = (index) => {
    if (details.length > 1) {
      const newDetails = details.filter((_, i) => i !== index);
      setDetails(newDetails);
      recalculateTotalsFromDetails(newDetails);
    }
  };

  const openStockModal = async (index) => {
    const detail = details[index];
    const productId = detail.StockTransferDet_Product_Id;
    
    if (!detail || !productId) {
      Swal.fire("Error", "Please select a product first", "error");
      return;
    }

    // Validate that From Branch is selected (mandatory for stock transfer)
    if (!form.StockTransfer_FromBranch_Id) {
      Swal.fire("Error", "Please select From Branch first", "error");
      return;
    }

    // Generate form ID when first product is opened
    if (!temporaryFormUniqueId) {
      const formId = generateTemporaryFormUniqueId();
      setTemporaryFormUniqueId(formId);
      console.log('🔒 Generated form ID for stock lock:', formId);
    }

    const product = products.find(p => (p.product_Id || p.Product_Id) === productId);
    setCurrentProduct(product);
    setCurrentDetailIndex(index);
    
    console.log('🔒 Opening stock modal with form ID:', temporaryFormUniqueId);
    
    // Use StockSelectionModal for stock selection with stock transfer API
    // The modal will use getStockForTransfer which filters by:
    // - LastTransFlag IN (1, 2, 8)
    // - Branch_Id = From Branch (mandatory)
    // - IsAvailable = 1
    setShowStockSelectionModal(true);
  };

  const handleStockSelected = (stockData) => {
    const detailIndex = currentDetailIndex;
    const detail = details[detailIndex];
    
    if (!detail) return;

    // Collect all selected stocks with their cost prices
    const selectedStocks = [];
    
    stockData.forEach(group => {
      group.stocks.forEach(stock => {
        selectedStocks.push({
          PurchaseStock_Id: stock.purchaseStock_Id || stock.PurchaseStock_Id,
          PurchaseStock_Product_Id: stock.purchaseStock_Product_Id || stock.PurchaseStock_Product_Id,
          PurchaseStock_Unit_Id: stock.purchaseStock_Unit_Id || stock.PurchaseStock_Unit_Id,
          PurchaseStock_BatchNo: stock.purchaseStock_BatchNo || stock.PurchaseStock_BatchNo,
          PurchaseStock_ExpDate: stock.purchaseStock_ExpDate || stock.PurchaseStock_ExpDate,
          PurchaseStock_CostPrice: stock.purchaseStock_CostPrice || stock.PurchaseStock_CostPrice || 0,
          PurchaseStock_SalesPrice: stock.purchaseStock_SalesPrice || stock.PurchaseStock_SalesPrice || 0,
          PurchaseStock_IsBonus: stock.purchaseStock_IsBonus || stock.PurchaseStock_IsBonus || false,
        });
      });
    });

    // Group stocks by cost price
    const stocksByCostPrice = {};
    selectedStocks.forEach(stock => {
      const costPrice = stock.PurchaseStock_CostPrice || 0;
      if (!stocksByCostPrice[costPrice]) {
        stocksByCostPrice[costPrice] = [];
      }
      stocksByCostPrice[costPrice].push(stock);
    });

    // Create separate detail rows for each cost price group
    const newDetails = [...details];
    const detailRows = [];
    
    Object.keys(stocksByCostPrice).forEach(costPrice => {
      const stocksForPrice = stocksByCostPrice[costPrice];
      const quantity = stocksForPrice.length;
      
      // Create new detail row with this cost price
      const newDetail = {
        ...detail,
        StockTransferDet_Id: 0, // New row
        StockTransferDet_Quantity: quantity,
        StockTransferDet_CostPrice: parseFloat(costPrice),
        StockDetails: stocksForPrice
      };
      
      detailRows.push(newDetail);
    });

    // Replace the current detail with the grouped rows
    newDetails.splice(detailIndex, 1, ...detailRows);

    // Recalculate amounts for each new detail row
    detailRows.forEach((_, idx) => {
      calculateDetailAmounts(newDetails, detailIndex + idx);
    });
    
    recalculateTotalsFromDetails(newDetails);
    setDetails(newDetails);

    // Store selected stock for this detail
    setSelectedStockItems(prev => ({
      ...prev,
      [detailIndex]: stockData
    }));

    setShowStockSelectionModal(false);
  };

  const openStockDetailsModal = (index) => {
    const detail = details[index];
    const productName = detail.Product_NameEn || 'Product';
    const stocks = detail.StockDetails || [];
    setCurrentStockDetailsProduct(productName);
    setCurrentStockDetails(stocks);
    setShowStockDetailsModal(true);
  };

  const calculateTotals = () => {
    if (isViewMode) {
      return;
    }
    recalculateTotalsFromDetails(details);
  };

  useEffect(() => {
    calculateTotals();
  }, [details, isViewMode]);

  // Cleanup stock locks on page refresh/navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Note: Modern browsers may ignore async operations in beforeunload
      // So we'll also cleanup on component unmount
      if (temporaryFormUniqueId) {
        // Try to cleanup synchronously if possible
        navigator.sendBeacon && navigator.sendBeacon(
          `${window.location.origin}/api/StockSelectionLock/delete-lock`,
          JSON.stringify({ temporaryFormUniqueId })
        );
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Cleanup on component unmount (page navigation, route change)
      if (temporaryFormUniqueId) {
        console.log('🔒 Cleaning up locks on component unmount:', temporaryFormUniqueId);
        deleteStockSelectionLock(temporaryFormUniqueId).catch(err => 
          console.error('Error cleaning up locks on unmount:', err)
        );
      }
    };
  }, [temporaryFormUniqueId]);

  const handleSave = async () => {
    // Validation
    if (!form.StockTransfer_Business_Id) {
      Swal.fire("Error", "Please select Company", "error");
      return;
    }
    
    if (!form.StockTransfer_FromBranch_Id) {
      Swal.fire("Error", "Please select From Branch", "error");
      return;
    }
    
    if (!form.StockTransfer_ToBranch_Id) {
      Swal.fire("Error", "Please select To Branch", "error");
      return;
    }
    
    if (form.StockTransfer_FromBranch_Id === form.StockTransfer_ToBranch_Id) {
      Swal.fire("Error", "From Branch and To Branch cannot be the same", "error");
      return;
    }
    
    if (!form.StockTransfer_TransferNo) {
      Swal.fire("Error", "Transfer number is required", "error");
      return;
    }
    
    // Validate details
    const validDetails = details.filter(d => d.StockTransferDet_Product_Id && d.StockTransferDet_Quantity > 0);
    if (validDetails.length === 0) {
      Swal.fire("Error", "Please add at least one product with quantity", "error");
      return;
    }
    
    // Check if all details have at least one stock selected
    for (const detail of validDetails) {
      if (!detail.StockDetails || !Array.isArray(detail.StockDetails) || detail.StockDetails.length === 0) {
        const productName = detail.Product_NameEn || 'product';
        Swal.fire("Error", `Please select at least one stock for ${productName}`, "error");
        return;
      }
    }

    // Check stock locks before save
    if (temporaryFormUniqueId) {
      // Collect all selected stock IDs
      const allStockIds = [];
      validDetails.forEach(detail => {
        if (detail.StockDetails && Array.isArray(detail.StockDetails)) {
          detail.StockDetails.forEach(stock => {
            const stockId = stock.PurchaseStock_Id || stock.purchaseStock_Id;
            if (stockId) {
              allStockIds.push(stockId.toString());
            }
          });
        }
      });
      
      if (allStockIds.length > 0) {
        try {
          const checkResult = await checkStockSelectionLocks(
            allStockIds.join(','),
            temporaryFormUniqueId
          );
          
          if (!checkResult.isSuccess || checkResult.hasLocks) {
            // Stocks are locked, prevent save and refresh stock modal
            Swal.fire({
              title: "Stock Locked",
              text: checkResult.message || "Some selected stocks are no longer available. Please refresh and select different stocks.",
              icon: "error",
              confirmButtonText: "OK"
            }).then(() => {
              // Refresh stock modal for the first detail with locked stock
              const firstDetailWithStock = validDetails.findIndex(detail => 
                detail.StockDetails && detail.StockDetails.length > 0
              );
              if (firstDetailWithStock >= 0) {
                openStockModal(firstDetailWithStock);
              }
            });
            return;
          }
        } catch (error) {
          console.error('Error checking stock locks:', error);
          // Continue with save if check fails (don't block user)
        }
      }
    }

    try {
      setSaveLoading(true);
      
      const session = getUserSession();
      
      // Prepare details for API - keep TaxDetails nested in each detail (API reads from detail.TaxDetails)
      const detailsForApi = validDetails.map((detail, index) => {
        const detailObj = {
          StockTransferDet_Id: detail.StockTransferDet_Id || 0,
          StockTransferDet_Product_Id: detail.StockTransferDet_Product_Id,
          StockTransferDet_Unit_Id: detail.StockTransferDet_Unit_Id,
          StockTransferDet_Quantity: detail.StockTransferDet_Quantity,
          StockTransferDet_CostPrice: detail.StockTransferDet_CostPrice,
          StockTransferDet_TaxAmount: detail.StockTransferDet_TaxAmount,
          StockTransferDet_TotalAmount: detail.StockTransferDet_TotalAmount,
          StockTransferDet_NetAmount: detail.StockTransferDet_NetAmount,
          // Only include TaxDetails if it exists and has items (avoid empty arrays)
          TaxDetails: (detail.TaxDetails && Array.isArray(detail.TaxDetails) && detail.TaxDetails.length > 0) 
            ? detail.TaxDetails 
            : null,
          // Only include StockDetails if it exists and has items (avoid empty arrays)
          StockDetails: (detail.StockDetails && Array.isArray(detail.StockDetails) && detail.StockDetails.length > 0) 
            ? detail.StockDetails 
            : null
        };
        
        return detailObj;
      });

      // Count totals for logging
      const totalTaxDetails = detailsForApi.reduce((sum, d) => {
        return sum + (d.TaxDetails && Array.isArray(d.TaxDetails) ? d.TaxDetails.length : 0);
      }, 0);
      
      const totalStockDetails = detailsForApi.reduce((sum, d) => {
        return sum + (d.StockDetails && Array.isArray(d.StockDetails) ? d.StockDetails.length : 0);
      }, 0);

      const transferData = {
        ...form,
        StockTransfer_CreatedBy: session.userId,
        Details: detailsForApi
      };

      console.log('=== SAVING STOCK TRANSFER ===');
      console.log('TransferNo:', transferData.StockTransfer_TransferNo);
      console.log('FromBranch:', transferData.StockTransfer_FromBranch_Id);
      console.log('ToBranch:', transferData.StockTransfer_ToBranch_Id);
      console.log('Total Details:', detailsForApi.length);
      console.log('Total TaxDetails:', totalTaxDetails);
      console.log('Total StockDetails:', totalStockDetails);
      console.log('Details with TaxDetails:', detailsForApi.filter(d => d.TaxDetails && d.TaxDetails.length > 0).length);
      console.log('Details with StockDetails:', detailsForApi.filter(d => d.StockDetails && d.StockDetails.length > 0).length);
      
      // Log each detail's structure
      detailsForApi.forEach((d, idx) => {
        console.log(`Detail ${idx + 1}:`, {
          Product_Id: d.StockTransferDet_Product_Id,
          Quantity: d.StockTransferDet_Quantity,
          TaxDetailsCount: d.TaxDetails ? d.TaxDetails.length : 0,
          StockDetailsCount: d.StockDetails ? d.StockDetails.length : 0,
          StockDetails: d.StockDetails ? d.StockDetails.map(s => ({ PurchaseStock_Id: s.PurchaseStock_Id })) : null
        });
      });

      const result = await saveStockTransfer(transferData);
      
      console.log('=== RESULT FROM API ===');
      console.log('Result:', result);
      console.log('Result type:', typeof result);
      console.log('Result keys:', Object.keys(result || {}));
      console.log('isSuccess:', result?.isSuccess);
      console.log('message:', result?.message);
      console.log('returnedId:', result?.returnedId);
      
      // Handle both camelCase and PascalCase response formats
      const isSuccess = result?.isSuccess ?? result?.IsSuccess ?? false;
      const message = result?.message ?? result?.Message ?? "Failed to save stock transfer";
      const returnedId = result?.returnedId ?? result?.ReturnedId ?? 0;
      
      if (isSuccess) {
        // Cleanup stock locks after successful save
        if (temporaryFormUniqueId) {
          try {
            await deleteStockSelectionLock(temporaryFormUniqueId);
          } catch (error) {
            console.error('Error cleaning up stock locks after save:', error);
          }
        }
        
        console.log('✅ Save successful! ReturnedId:', returnedId);
        Swal.fire("Success", message || "Stock transfer saved successfully", "success");
        handleClear();
        loadStockTransfers();
        setActiveTab("2");
      } else {
        console.log('❌ Save failed! Message:', message);
        Swal.fire("Error", message || "Failed to save stock transfer", "error");
      }
    } catch (error) {
      console.error("=== ERROR SAVING STOCK TRANSFER ===");
      console.error("Error:", error);
      console.error("Error Response:", error.response);
      console.error("Error Data:", error.response?.data);
      console.error("Error Message:", error.message);
      console.error("Error Stack:", error.stack);
      
      const errorMessage = error.response?.data?.message || error.message || "Failed to save stock transfer";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    // Cleanup stock locks before clear
    if (temporaryFormUniqueId) {
      deleteStockSelectionLock(temporaryFormUniqueId).catch(err => 
        console.error('Error cleaning up locks on clear:', err)
      );
    }
    
    setForm({
      ...defaultForm,
      StockTransfer_Date: new Date()
    });
    setDetails([defaultDetail]);
    setIsEditing(false);
    setIsViewMode(false);
    setTemporaryFormUniqueId(null);
    setFormKey(prev => prev + 1);
  };

  const handleEdit = async (transfer) => {
    try {
      setEditLoadingRow(transfer.stockTransfer_Id);
      
      // Load full transfer details
      const filter = { StockTransfer_Id: transfer.stockTransfer_Id };
      const data = await getStockTransfers(filter);
      const transferData = Array.isArray(data) ? data[0] : (data.data || [])[0];
      
      if (!transferData) {
        Swal.fire("Error", "Transfer not found", "error");
        return;
      }
      
      setForm({
        StockTransfer_Id: transferData.stockTransfer_Id,
        StockTransfer_TransferNo: transferData.stockTransfer_TransferNo,
        StockTransfer_FromBranch_Id: transferData.stockTransfer_FromBranch_Id,
        StockTransfer_ToBranch_Id: transferData.stockTransfer_ToBranch_Id,
        StockTransfer_Date: transferData.stockTransfer_Date ? new Date(transferData.stockTransfer_Date) : new Date(),
        StockTransfer_TotalAmount: transferData.stockTransfer_TotalAmount || 0,
        StockTransfer_TaxAmount: transferData.stockTransfer_TaxAmount || 0,
        StockTransfer_NetAmount: transferData.stockTransfer_NetAmount || 0,
        StockTransfer_TotalQty: transferData.stockTransfer_TotalQty || 0,
        StockTransfer_Notes: transferData.stockTransfer_Notes || "",
        StockTransfer_Business_Id: transferData.stockTransfer_Business_Id,
        Details: []
      });
      
      // Map details
      if (transferData.details && transferData.details.length > 0) {
        const mappedDetails = transferData.details.map((detail) => ({
          StockTransferDet_Id: detail.stockTransferDet_Id || 0,
          StockTransferDet_Product_Id: detail.stockTransferDet_Product_Id,
          StockTransferDet_Unit_Id: detail.stockTransferDet_Unit_Id,
          StockTransferDet_Quantity: detail.stockTransferDet_Quantity || 0,
          StockTransferDet_CostPrice: detail.stockTransferDet_CostPrice || 0,
          StockTransferDet_TaxAmount: detail.stockTransferDet_TaxAmount || 0,
          StockTransferDet_TotalAmount: detail.stockTransferDet_TotalAmount || 0,
          StockTransferDet_NetAmount: detail.stockTransferDet_NetAmount || 0,
          Product_NameEn: detail.product_NameEn || '',
          Unit_NameEn: detail.unit_NameEn || '',
          TaxDetails: detail.taxDetails || [],
          StockDetails: detail.stockDetails || []
        }));
        setDetails(mappedDetails.length > 0 ? mappedDetails : [defaultDetail]);
      } else {
        setDetails([defaultDetail]);
      }
      
      setIsEditing(true);
      setIsViewMode(false);
      setActiveTab("1");
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error loading transfer for edit:", error);
      Swal.fire("Error", "Failed to load transfer for editing", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleView = async (transfer) => {
    try {
      setEditLoadingRow(transfer.stockTransfer_Id);
      
      const filter = { StockTransfer_Id: transfer.stockTransfer_Id };
      const data = await getStockTransfers(filter);
      const transferData = Array.isArray(data) ? data[0] : (data.data || [])[0];
      
      if (!transferData) {
        Swal.fire("Error", "Transfer not found", "error");
        return;
      }
      
      setForm({
        StockTransfer_Id: transferData.stockTransfer_Id,
        StockTransfer_TransferNo: transferData.stockTransfer_TransferNo,
        StockTransfer_FromBranch_Id: transferData.stockTransfer_FromBranch_Id,
        StockTransfer_ToBranch_Id: transferData.stockTransfer_ToBranch_Id,
        StockTransfer_Date: transferData.stockTransfer_Date ? new Date(transferData.stockTransfer_Date) : new Date(),
        StockTransfer_TotalAmount: transferData.stockTransfer_TotalAmount || 0,
        StockTransfer_TaxAmount: transferData.stockTransfer_TaxAmount || 0,
        StockTransfer_NetAmount: transferData.stockTransfer_NetAmount || 0,
        StockTransfer_TotalQty: transferData.stockTransfer_TotalQty || 0,
        StockTransfer_Notes: transferData.stockTransfer_Notes || "",
        StockTransfer_Business_Id: transferData.stockTransfer_Business_Id,
        Details: []
      });
      
      if (transferData.details && transferData.details.length > 0) {
        const mappedDetails = transferData.details.map((detail) => ({
          StockTransferDet_Id: detail.stockTransferDet_Id || 0,
          StockTransferDet_Product_Id: detail.stockTransferDet_Product_Id,
          StockTransferDet_Unit_Id: detail.stockTransferDet_Unit_Id,
          StockTransferDet_Quantity: detail.stockTransferDet_Quantity || 0,
          StockTransferDet_CostPrice: detail.stockTransferDet_CostPrice || 0,
          StockTransferDet_TaxAmount: detail.stockTransferDet_TaxAmount || 0,
          StockTransferDet_TotalAmount: detail.stockTransferDet_TotalAmount || 0,
          StockTransferDet_NetAmount: detail.stockTransferDet_NetAmount || 0,
          Product_NameEn: detail.product_NameEn || '',
          Unit_NameEn: detail.unit_NameEn || '',
          TaxDetails: detail.taxDetails || [],
          StockDetails: detail.stockDetails || []
        }));
        setDetails(mappedDetails);
      } else {
        setDetails([defaultDetail]);
      }
      
      setIsEditing(false);
      setIsViewMode(true);
      setActiveTab("1");
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error loading transfer for view:", error);
      Swal.fire("Error", "Failed to load transfer", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilter = () => {
    setPage(1);
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    setHasAppliedFilter(true);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.StockTransfer_Id !== null && filter.StockTransfer_Id !== undefined && { StockTransfer_Id: filter.StockTransfer_Id }),
        ...(filter.StockTransfer_TransferNo && filter.StockTransfer_TransferNo.trim() !== '' && { StockTransfer_TransferNo: filter.StockTransfer_TransferNo }),
        ...(filter.FromBranch_Id !== null && filter.FromBranch_Id !== undefined && { FromBranch_Id: filter.FromBranch_Id }),
        ...(filter.ToBranch_Id !== null && filter.ToBranch_Id !== undefined && { ToBranch_Id: filter.ToBranch_Id }),
        ...(filter.Business_Id !== null && filter.Business_Id !== undefined && { Business_Id: filter.Business_Id }),
        ...(filter.IsReceived !== null && filter.IsReceived !== undefined && { IsReceived: filter.IsReceived }),
        ...(filter.FromDate && { FromDate: filter.FromDate }),
        ...(filter.ToDate && { ToDate: filter.ToDate })
      };
      
      await exportStockTransfersToExcel(cleanFilter);
      Swal.fire("Success", "Stock transfer report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting stock transfers:", error);
      Swal.fire("Error", "Failed to export stock transfer report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportDetails = async (stockTransferId) => {
    try {
      setLoading(true);
      await exportStockTransferDetailsToExcel(stockTransferId);
      Swal.fire("Success", "Stock transfer details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting stock transfer details:", error);
      Swal.fire("Error", "Failed to export stock transfer details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data
  const getPaginatedTransfers = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return stockTransfers.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (!hasPermission) {
    return null;
  }

  const breadcrumbItem = activeTab === "1" ? "Add Stock Transfer" : "Stock Transfer List";

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Stock Transfer Management" breadcrumbItem={breadcrumbItem} />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Stock Transfer</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Stock Transfer List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        {/* Basic Information */}
                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Company <span className="text-danger">*</span></Label>
                              <Select
                                key={`business-${formKey}`}
                                value={businessOptions.find(option => option.value === form.StockTransfer_Business_Id)}
                                onChange={(option) => handleBusinessChange(option?.value || null)}
                                options={businessOptions}
                                placeholder="Select Company"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>From Branch <span className="text-danger">*</span></Label>
                              <Select
                                key={`fromBranch-${formKey}`}
                                value={branchOptions.find(option => option.value === form.StockTransfer_FromBranch_Id)}
                                onChange={(option) => {
                                  console.log("🖱️ From Branch Select onChange triggered");
                                  console.log("Selected option:", option);
                                  console.log("Option value:", option?.value);
                                  console.log("Current form.StockTransfer_FromBranch_Id:", form.StockTransfer_FromBranch_Id);
                                  handleFromBranchChange(option?.value || null);
                                }}
                                options={branchOptions}
                                placeholder="Select From Branch"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={isViewMode || !form.StockTransfer_Business_Id}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>To Branch <span className="text-danger">*</span></Label>
                              <Select
                                key={`toBranch-${formKey}`}
                                value={branchOptions.find(option => option.value === form.StockTransfer_ToBranch_Id)}
                                onChange={(option) => {
                                  // Validate before setting
                                  if (option?.value === form.StockTransfer_FromBranch_Id) {
                                    Swal.fire("Error", "To Branch cannot be the same as From Branch", "error");
                                    return;
                                  }
                                  handleFormChange("StockTransfer_ToBranch_Id", option?.value || null);
                                }}
                                options={branchOptions}
                                placeholder="Select To Branch"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={isViewMode || !form.StockTransfer_Business_Id}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Transfer Number</Label>
                              <Input
                                type="text"
                                value={form.StockTransfer_TransferNo}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Date <span className="text-danger">*</span></Label>
                              <DatePicker
                                selected={form.StockTransfer_Date}
                                onChange={(date) => handleFormChange("StockTransfer_Date", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                        </Row>

                        {/* Details Section */}
                        <Row>
                          <Col md={12}>
                            <Label className="fw-bold mb-3">Transfer Details</Label>
                            <div className="table-responsive">
                              <Table bordered style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th width="4%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Sr.No</th>
                                    <th width="24%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Product</th>
                                    <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit</th>
                                    <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Qty</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Cost Price</th>
                                    <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Tax %</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Tax Amount</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Total Amount</th>
                                    <th width="11%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Net Amount</th>
                                    <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {details
                                    .filter(detail => detail && detail.StockTransferDet_Product_Id !== undefined)
                                    .map((detail, index) => {
                                    return (
                                    <tr key={index}>
                                      <td style={{ padding: '0', textAlign: 'center', border: '1px solid #dee2e6' }}>{index + 1}</td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Select
                                          value={productOptions.find(option => option.value === detail.StockTransferDet_Product_Id)}
                                          onChange={(option) => handleDetailChange(index, 'StockTransferDet_Product_Id', option?.value || null)}
                                          options={productOptions}
                                          placeholder="Select Product"
                                          styles={{
                                            ...selectStyles,
                                            control: (provided) => ({
                                              ...provided,
                                              border: 'none',
                                              borderRadius: '0',
                                              minHeight: '38px',
                                              boxShadow: 'none'
                                            })
                                          }}
                                          isClearable
                                          isSearchable
                                          maxMenuHeight={200}
                                          isDisabled={isViewMode}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="text"
                                          value={detail.Unit_NameEn || ''}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                          placeholder="Select Product"
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={detail.StockTransferDet_Quantity}
                                          onChange={(e) => handleDetailChange(index, 'StockTransferDet_Quantity', parseFloat(e.target.value) || 0)}
                                          disabled={isViewMode}
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="any"
                                          value={detail.StockTransferDet_CostPrice}
                                          onChange={(e) => handleDetailChange(index, 'StockTransferDet_CostPrice', parseFloat(e.target.value) || 0)}
                                          disabled={isViewMode}
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="text"
                                          value={detail.TaxDetails && detail.TaxDetails.length > 0 
                                            ? detail.TaxDetails.map(t => `${t.Tax_Perc || 0}%`).join(', ')
                                            : '0%'}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={detail.StockTransferDet_TaxAmount}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={detail.StockTransferDet_TotalAmount || 0}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={detail.StockTransferDet_NetAmount || 0}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '4px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', width: '4%' }}>
                                        <div className="d-flex justify-content-center align-items-center" style={{ gap: '4px', minHeight: '38px', flexWrap: 'nowrap' }}>
                                          {!isViewMode && (
                                            <Button
                                              color="warning"
                                              size="sm"
                                              onClick={() => openStockModal(index)}
                                              disabled={!detail.StockTransferDet_Product_Id}
                                              style={{ 
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                minWidth: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}
                                              title="Select Stock"
                                            >
                                              <i className="bx bx-package" style={{ fontSize: '16px' }}></i>
                                            </Button>
                                          )}
                                          {isViewMode && detail.StockDetails && detail.StockDetails.length > 0 && (
                                            <Button
                                              color="info"
                                              size="sm"
                                              onClick={() => openStockDetailsModal(index)}
                                              style={{ 
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                minWidth: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}
                                              title="View Stock"
                                            >
                                              <i className="bx bx-show" style={{ fontSize: '16px' }}></i>
                                            </Button>
                                          )}
                                          {!isViewMode && (
                                            <Button
                                              color="danger"
                                              size="sm"
                                              onClick={() => removeDetail(index)}
                                              style={{ 
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                minWidth: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}
                                              disabled={details.length === 1}
                                              title="Remove"
                                            >
                                              <X size={14} />
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )})}
                                </tbody>
                              </Table>
                            </div>
                            {!isViewMode && (
                              <Button
                                color="primary"
                                size="sm"
                                onClick={addDetail}
                                className="mt-2"
                              >
                                <Plus size={14} className="me-1" />
                                Add Product
                              </Button>
                            )}
                          </Col>
                        </Row>

                        {/* Totals Section */}
                        <Row className="mt-3">
                          <Col md={6}>
                            <div className="mb-3">
                              <Label>Notes</Label>
                              <Input
                                type="textarea"
                                rows="3"
                                value={form.StockTransfer_Notes}
                                onChange={(e) => handleFormChange("StockTransfer_Notes", e.target.value)}
                                placeholder="Enter notes..."
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Table bordered>
                                <tbody>
                                  <tr>
                                    <td><strong>Total Qty:</strong></td>
                                    <td className="text-end">{form.StockTransfer_TotalQty.toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Total Amount:</strong></td>
                                    <td className="text-end">{form.StockTransfer_TotalAmount.toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Tax Amount:</strong></td>
                                    <td className="text-end">{form.StockTransfer_TaxAmount.toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Net Amount:</strong></td>
                                    <td className="text-end"><strong>{form.StockTransfer_NetAmount.toFixed(2)}</strong></td>
                                  </tr>
                                </tbody>
                              </Table>
                            </div>
                          </Col>
                        </Row>

                        {/* Action Buttons */}
                        {!isViewMode && (
                          <Row>
                            <Col md={12} className="text-end">
                              <Button
                                color="secondary"
                                className="me-2"
                                onClick={handleClear}
                                disabled={saveLoading}
                              >
                                Clear
                              </Button>
                              <Button
                                color="primary"
                                type="submit"
                                disabled={saveLoading}
                              >
                                {saveLoading ? (
                                  <>
                                    <ClipLoader size={14} color="#ffffff" className="me-2" />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <i className="bx bx-save me-1"></i>
                                    {isEditing ? "Update Transfer" : "Transfer"}
                                  </>
                                )}
                              </Button>
                            </Col>
                          </Row>
                        )}

                        {isViewMode && (
                          <Row className="mt-4">
                            <Col>
                              <Button
                                type="button"
                                color="primary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Stock Transfer
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">Stock Transfer List</h5>
                            <div className="d-flex gap-2">
                              <Button
                                color="success"
                                onClick={handleExportExcel}
                                disabled={loading || totalCount === 0}
                                className="waves-effect waves-light"
                              >
                                <Download className="me-1" size={16} />
                                Export Excel
                              </Button>
                              {hasEditOrHigher && (
                                <Button
                                  color="primary"
                                  onClick={() => setActiveTab("1")}
                                  className="waves-effect waves-light"
                                >
                                  <i className="bx bx-plus me-1"></i>
                                  Add New Transfer
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading transfers...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="4%">#</th>
                                      <th width="12%">Date</th>
                                      <th width="15%">Transfer Number</th>
                                      <th width="15%">From Branch</th>
                                      <th width="15%">To Branch</th>
                                      <th width="10%">Net Amount</th>
                                      <th width="10%">Status</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getPaginatedTransfers().length > 0 ? (
                                      getPaginatedTransfers().map((item, index) => (
                                        <tr key={`transfer-${item.stockTransfer_Id || index}`}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{item.stockTransfer_Date ? formatDateDDMMYYYY(item.stockTransfer_Date) : 'N/A'}</td>
                                          <td>{item.stockTransfer_TransferNo || 'N/A'}</td>
                                          <td>{item.fromBranch_NameEn || 'N/A'}</td>
                                          <td>{item.toBranch_NameEn || 'N/A'}</td>
                                          <td>{Number(item.stockTransfer_NetAmount ?? 0).toFixed(2)}</td>
                                          <td>
                                            <span className={`badge ${item.stockTransfer_IsReceived ? 'bg-success' : 'bg-warning'}`}>
                                              {item.stockTransfer_IsReceived ? 'Received' : 'Pending'}
                                            </span>
                                          </td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                                title="View"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => handleExportDetails(item.stockTransfer_Id)}
                                                disabled={loading}
                                                className="waves-effect waves-light"
                                                title="Export Details"
                                              >
                                                <Download size={12} />
                                              </Button>
                                              {/* Edit icon - Commented out for now */}
                                              {/* {hasEditOrHigher && !item.stockTransfer_IsReceived && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === item.stockTransfer_Id}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === item.stockTransfer_Id ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )} */}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="8" className="text-center">
                                          {hasAppliedFilter ? "No transfers found matching the filter criteria." : "No transfers available. Please add some transfers first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {totalPages}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === totalPages}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Stock Selection Modal */}
      <StockSelectionModal
        isOpen={showStockSelectionModal}
        toggle={() => setShowStockSelectionModal(false)}
        showCostPriceColumn={true}
        productId={currentProduct ? (currentProduct.product_Id || currentProduct.Product_Id) : null}
        productName={currentProduct ? (currentProduct.product_NameEn || currentProduct.Product_NameEn) : ''}
        requestedQuantity={details[currentDetailIndex]?.StockTransferDet_Quantity || 0}
        onStockSelected={handleStockSelected}
        selectedStockItems={selectedStockItems[currentDetailIndex] || []}
        branchId={form.StockTransfer_FromBranch_Id}
        businessId={form.StockTransfer_Business_Id}
        customFetchStock={getStockForTransfer}  // Use stock transfer specific API
        temporaryFormUniqueId={temporaryFormUniqueId}
        flag={5}
        createdBy={getUserSession().userId}
      />

      {/* Stock Details Modal (View Only) */}
      <StockDetailsModal
        isOpen={showStockDetailsModal}
        toggle={() => setShowStockDetailsModal(false)}
        productName={currentStockDetailsProduct}
        stockDetails={currentStockDetails}
      />
    </div>
  );
}

